import { createSelector } from 'reselect';

// Selectors
import { createShallowSelector } from '../../../../../../utils/milanoteReselect/milanoteReselect';
import { getCurrentBoardId, getCurrentBoard } from '../../../../../../reducers/currentBoardId/currentBoardIdSelector';
import { getIsCurrentBoardRoot } from '../../../../../../element/board/boardSelector';
import { popupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { getGridSize } from '../../../../../../utils/grid/gridSizeSelector';
import { filterWords, removeSpecialCharacters } from '../../../../../../components/elementIcon/elementIconUtils';

import { IMAGE_SEARCH_POPUP_MODE_DEFAULT, IMAGE_SEARCH_POPUP_MODE_UPLOAD } from './imageSearchPopupConstants';
import { DEFAULT_IMAGE_SEARCH_QUERY } from '../../../../../../../server/app/components/images/imageSearchConstants';

const getSearchPopup = popupSelector('imageSearch');

const getPopupMode = (state) => getSearchPopup(state).get('mode') || IMAGE_SEARCH_POPUP_MODE_DEFAULT;

const popupStateSelector = createSelector(getSearchPopup, (popup) => ({
    loading: popup.get('loading'),
    submittedSearchQuery: popup.get('submittedQuery'),
    error: popup.get('error'),
}));

const defaultSearchQuerySelector = createSelector(getCurrentBoard, getIsCurrentBoardRoot, (currentBoard, isRootBoard) =>
    isRootBoard ? DEFAULT_IMAGE_SEARCH_QUERY : currentBoard?.getIn(['content', 'title']) || '',
);

const searchTermSelector = createSelector(getPopupMode, defaultSearchQuerySelector, (mode, defaultTerm) => {
    const searchTerm = mode === IMAGE_SEARCH_POPUP_MODE_DEFAULT ? defaultTerm : mode;

    return (searchTerm || '').toLowerCase();
});

const searchResultsListSelector = createShallowSelector(getSearchPopup, searchTermSelector, (popup, selectedTerm) =>
    popup.getIn(['results', selectedTerm]) ? popup.getIn(['results', selectedTerm]).toArray() : [],
);

const searchResultsHasMoreSelector = createSelector(
    getSearchPopup,
    searchTermSelector,
    searchResultsListSelector,
    (popup, selectedTerm, resultsList) => {
        const totalCount = popup.getIn(['resultsTotalCount', selectedTerm]) || 0;

        return resultsList.length < totalCount;
    },
);

const makeModeListEntry = (word) => ({ slug: word, title: word });

const recentSearchListSelector = createSelector(getSearchPopup, defaultSearchQuerySelector, (popup, defaultSearch) => {
    // These search suggestions are probably just
    // placeholders until we get the NLP stuff in place
    // so not a great deal of effort here to be elegant or clever
    const splitTerms = defaultSearch.split(' ');

    const searchSuggestions =
        splitTerms.length > 1
            ? splitTerms
                  .filter(filterWords())
                  .map(removeSpecialCharacters)
                  .filter((word) => word.length > 0)
            : [];
    // ---

    const searchHistory = popup
        .get('recentSearchList')
        .filter((word) => searchSuggestions.indexOf(word) === -1)
        .takeLast(10 - searchSuggestions.length);

    return [
        { slug: IMAGE_SEARCH_POPUP_MODE_DEFAULT, title: 'Recommended' },
        ...searchSuggestions.map(makeModeListEntry),
        ...searchHistory.map(makeModeListEntry),
        { slug: IMAGE_SEARCH_POPUP_MODE_UPLOAD, title: 'Upload your own' },
    ];
});

const searchPopupSelector = createSelector(
    popupStateSelector,
    searchResultsListSelector,
    getCurrentBoardId,
    getGridSize,
    recentSearchListSelector,
    getPopupMode,
    searchTermSelector,
    searchResultsHasMoreSelector,
    (popupState, searchResultsList, currentBoardId, gridSize, modeList, popupMode, searchQuery, hasMoreResults) => ({
        ...popupState,
        searchResultsList,
        currentBoardId,
        gridSize,
        popupMode,
        modeList,
        searchQuery,
        hasMoreResults,
    }),
);

export default searchPopupSelector;
